import { IAppContext } from 'App/AppContext';
import {
  globalRegExKPIExpressionFuncSubTaskMultiply,
  globalRegExKPIExpressionFuncSubTaskSum,
  globalRegExStripKPIExpression,
} from 'globalConstants';
import { TFunction } from 'i18next';
import Joi from 'joi';
import { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import StringValue from 'models/stringValue/stringValue';
import Task from 'models/tasks/task';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { areDifferent } from 'utils/array';
import { expression_eval } from 'utils/expression';
import { KPI_Translation } from './kpi_Translation';
import KPIAlert from './KPIAlert';
import KPIData from './kpiData';
import KPIDataContext, { KPIContextFilter } from './kpiDataContext';
import KPIGraphData from './kpiGraphData';
import { KPIUsageKind } from './kpiUsageKind';

//apiRequest must be above apiGetKPIName otherwise circular dependency (TS error): should be fixed
import { apiGetKPIName } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';

export enum KPITypes {
  NotSet = 0,
  Text = 1,
  TextMultiline = 2,
  Html = 3,
  Number = 4,
  Choice = 5,
  Date = 6,
  SuccesError = 7,
  Expression = 99,
}

export enum KPITypesSystem {
  NotSet = 0,
  Task = 1,
  //the KPI of a SkillControl check (phase 20R2): locked against definition edits and data edits, its data points are
  //ordinary ones the pushed results wrote
  ExternalCheck = 99,
}

export enum KPIAggregationMethods {
  Count = 0,
  Sum = 1,
  Min = 2,
  Max = 3,
  Avg = 4,
  Last = 5,
}

export enum KPIAggregationIntervals {
  Day = 1,
  Week = 2,
  Month = 3,
  Quarter = 4,
  Year = 5,
}

export enum KPIChartTypes {
  Bar = 1,
  Line = 2,
  Area = 3,
  Grid = 4,
}

export default class KPI {
  kpiId: number;

  currentDefinitionRevisionId?: number;

  currentDefinitionRevisionNumber?: number;

  ownerTaskTypeFamilyId?: number;

  rowVersion?: string;

  authSchemaId?: number;

  auditTrailId: number | undefined;

  type: number;

  minValue: string | undefined;

  maxValue: string | undefined;

  decimalCount: number | undefined;

  defValue: string | undefined;

  required: boolean;

  attachmentMode?: number;

  listId?: number;

  folderId?: string;

  commentMode?: number;

  systemKPIType?: KPITypesSystem;

  isSystem: boolean;

  isField: boolean;

  showLabel: boolean;

  trans: KPI_Translation[];

  // Flattened from Trans
  transIdx: number;

  name: string;

  description?: string;

  instruction?: string;

  expectedResult?: string;

  hasAutomatedEvidence: boolean;

  //relations
  tagIds: number[];

  controlIds?: number[];

  objectiveIds?: number[];

  alerts: KPIAlert[];

  taskTypeIds: number[];

  dashboardIds: number[];

  integrationIds: number[];

  // the distinct kinds of use the KPI list computed; read-only list data, not part of a save
  usageKinds: KPIUsageKind[];

  // the number of rows the usage modal lists, computed by the KPI list
  usageCount: number;

  choices?: StringValue[];

  // internals
  private _data: KPIData[]; //protect because each data record must have a ref to this kpi

  graphData?: KPIGraphData;

  //
  // Properties to store the values based on the definition
  //
  attachments?: ResourceLink[];

  constructor() {
    this.kpiId = 0;
    this.auditTrailId = 0;
    this.trans = [];
    this.name = '';
    this.transIdx = -1;
    this.type = KPITypes.Text;
    this.required = false;
    this.tagIds = [];
    this.commentMode = 0;
    this.attachmentMode = 0;
    this.alerts = [];
    this.hasAutomatedEvidence = false;
    this.taskTypeIds = [];
    this.dashboardIds = [];
    this.integrationIds = [];
    this.usageKinds = [];
    this.usageCount = 0;
    this.showLabel = true;
    this.isSystem = false;
    this.isField = false;

    //add a default data record with a reference to this KPI
    const newData = new KPIData();
    newData.kpi = this;
    newData.kpiId = this.kpiId;
    this._data = [newData];
  }

  //
  // Data accessors
  //
  get data(): KPIData[] {
    return this._data;
  }

  set data(newData: KPIData[]) {
    this._data = newData;
    this._data.forEach((d) => (d.kpi = this));
  }

  addData(newData: KPIData[]) {
    this._data.push(...newData);
    this._data.forEach((d) => (d.kpi = this));
  }

  //
  // Trans helpers
  //
  getTransIdx(langCode: string, defLangCode: string): number {
    if (this.trans) {
      //find requested language
      for (let i = 0; i < this.trans.length; i++) {
        const t: KPI_Translation = this.trans[i];
        if (t.lang?.code === langCode) {
          return i;
        }
      }

      //if not found, find the default language
      for (let i = 0; i < this.trans.length; i++) {
        const t: KPI_Translation = this.trans[i];
        if (t.lang?.code === defLangCode) {
          return i;
        }
      }
    }

    return -1;
  }

  getTypeText(t: TFunction<string[]>): string {
    if (this.isSystemType()) {
      return t('kpis:Types.System');
    }

    switch (this.type) {
      case KPITypes.Choice:
        return t('kpis:Types.Choice');
      case KPITypes.TextMultiline:
        return t('kpis:Types.TextMultiline');
      case KPITypes.SuccesError:
        return t('kpis:Types.SuccesError');
      case KPITypes.Html:
        return t('kpis:Types.Html');
      case KPITypes.Number:
        return t('kpis:Types.Number');
      case KPITypes.Text:
        return t('kpis:Types.Text');
      case KPITypes.Date:
        return t('kpis:Types.Date');
      case KPITypes.Expression:
        return t('kpis:Types.Expression');
      default:
        return 'Unknown';
    }
  }

  static getAggregationMethodText(method: KPIAggregationMethods, t: TFunction<string[]>): string {
    switch (method) {
      case KPIAggregationMethods.Count:
        return t('kpis:AggregationMethods.Count');
      case KPIAggregationMethods.Min:
        return t('kpis:AggregationMethods.Min');
      case KPIAggregationMethods.Max:
        return t('kpis:AggregationMethods.Max');
      case KPIAggregationMethods.Avg:
        return t('kpis:AggregationMethods.Avg');
      case KPIAggregationMethods.Sum:
        return t('kpis:AggregationMethods.Sum');
      case KPIAggregationMethods.Last:
        return t('kpis:AggregationMethods.Last');
    }
  }

  static getAggregationIntervalText(interval: KPIAggregationIntervals, t: TFunction<string[]>): string {
    switch (interval) {
      case KPIAggregationIntervals.Day:
        return t('kpis:AggregationIntervals.Day');
      case KPIAggregationIntervals.Week:
        return t('kpis:AggregationIntervals.Week');
      case KPIAggregationIntervals.Month:
        return t('kpis:AggregationIntervals.Month');
      case KPIAggregationIntervals.Quarter:
        return t('kpis:AggregationIntervals.Quarter');
      case KPIAggregationIntervals.Year:
        return t('kpis:AggregationIntervals.Year');
    }
  }

  static getChartTypeText(type: KPIChartTypes, t: TFunction<string[]>): string {
    switch (type) {
      case KPIChartTypes.Bar:
        return t('kpis:ChartTypes.Bar');
      case KPIChartTypes.Area:
        return t('kpis:ChartTypes.Area');
      case KPIChartTypes.Line:
        return t('kpis:ChartTypes.Line');
      case KPIChartTypes.Grid:
        return t('kpis:ChartTypes.Grid');
    }
  }

  static expressionNeedsSubtasks(exp: string | undefined): boolean {
    if (exp) {
      const matchesSubSum = [...exp.matchAll(globalRegExKPIExpressionFuncSubTaskSum)];
      if (matchesSubSum.length > 0) return true;
      const matchesSubMultiply = [...exp.matchAll(globalRegExKPIExpressionFuncSubTaskMultiply)];
      if (matchesSubMultiply.length > 0) return true;
    }

    return false;
  }

  static calcExpression(task: Task, exp: string | undefined): number | undefined {
    try {
      if (exp && task.kpiData) {
        const data = task.kpiData;
        let newExp = exp;

        //replace subtask formula SUM
        const matchesSubSum = [...newExp.matchAll(globalRegExKPIExpressionFuncSubTaskSum)];
        for (let idx = matchesSubSum.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesSubSum[idx];
          if (match.index !== undefined) {
            let idStr = match[0];
            idStr = idStr.substring(12, idStr.length - 1);
            const id = Number.parseInt(idStr);
            let subResult: number = 0;

            if (task.instances) {
              for (let subIdx = 0; subIdx < task.instances.length; subIdx++) {
                const subData = task.instances[subIdx].kpiData;
                if (subData) {
                  const kpiData = subData.find((k) => k.kpiId === id);
                  if (kpiData && kpiData.resultNumber) {
                    subResult += kpiData.resultNumber;
                  }
                }
              }
            }

            if (subResult) {
              //found the KPI with data
              newExp =
                newExp.substring(0, match.index) +
                subResult.toString() +
                newExp.substring(match.index + idStr.length + 13);
            } else {
              //replace the match with zero
              newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length + 13);
            }
          }
        }

        //replace subtask formula MULTIPLY
        const matchesSubMultiply = [...newExp.matchAll(globalRegExKPIExpressionFuncSubTaskMultiply)];
        for (let idx = matchesSubMultiply.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesSubMultiply[idx];
          if (match.index !== undefined) {
            let idStr = match[0];
            idStr = idStr.substring(17, idStr.length - 1);
            const id = Number.parseInt(idStr);
            let subResult: number = 0;

            if (task.instances) {
              for (let subIdx = 0; subIdx < task.instances.length; subIdx++) {
                const subData = task.instances[subIdx].kpiData;
                if (subData) {
                  const kpiData = subData.find((k) => k.kpiId === id);
                  if (kpiData && kpiData.resultNumber) {
                    subResult *= kpiData.resultNumber;
                  }
                }
              }
            }

            if (subResult) {
              //found the KPI with data
              newExp =
                newExp.substring(0, match.index) +
                subResult.toString() +
                newExp.substring(match.index + idStr.length + 18);
            } else {
              //replace the match with zero
              newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length + 18);
            }
          }
        }

        //replace single KPI id's
        const matchesKPI = [...newExp.matchAll(globalRegExStripKPIExpression)];

        for (let idx = matchesKPI.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesKPI[idx];
          if (match.index !== undefined) {
            let idStr = match[0];
            idStr = idStr.substring(1, idStr.length - 1);
            const id = Number.parseInt(idStr);
            const kpiData = data.find(
              (k) => k.kpiId === id && k.kpi?.type !== KPITypes.Expression, //no expressions, this can cause infinite loops
            );
            if (kpiData && kpiData.resultNumber) {
              //found the KPI with data
              newExp =
                newExp.substring(0, match.index) +
                kpiData.resultNumber.toString() +
                newExp.substring(match.index + idStr.length + 2);
            } else {
              //replace the match with zero
              newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length + 2);
            }
          }
        }

        //evaluate expression
        const result = expression_eval(newExp);
        if (isNaN(result)) {
          return NaN;
        } else {
          return result;
        }
      } else {
        return undefined;
      }
    } catch {
      return NaN;
    }
  }

  static calcPreviewExpression(exp: string | undefined): number | undefined {
    try {
      if (exp) {
        let newExp = exp;

        //replace subtask formula SUM
        const matchesSubSum = [...newExp.matchAll(globalRegExKPIExpressionFuncSubTaskSum)];
        for (let idx = matchesSubSum.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesSubSum[idx];
          if (match.index !== undefined) {
            const idStr = match[0];
            newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length);
          }
        }

        //replace subtask formula MULTIPLY
        const matchesSubMultiply = [...newExp.matchAll(globalRegExKPIExpressionFuncSubTaskMultiply)];
        for (let idx = matchesSubMultiply.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesSubMultiply[idx];
          if (match.index !== undefined) {
            const idStr = match[0];
            newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length);
          }
        }

        //replace single KPI id's
        const matchesKPI = [...newExp.matchAll(globalRegExStripKPIExpression)];

        for (let idx = matchesKPI.length - 1; idx >= 0; idx--) {
          //backwards so we don't mess up indexes
          const match = matchesKPI[idx];
          if (match.index !== undefined) {
            const idStr = match[0];
            newExp = newExp.substring(0, match.index) + '0' + newExp.substring(match.index + idStr.length);
          }
        }

        //evaluate expression
        const result = expression_eval(newExp);
        if (isNaN(result)) {
          return NaN;
        } else {
          return result;
        }
      } else {
        return undefined;
      }
    } catch {
      return NaN;
    }
  }

  resetTypeConfig() {
    this.choices = undefined;
    this.decimalCount = 0;
    this.required = false;
    this.minValue = undefined;
    this.maxValue = undefined;
    this.defValue = undefined;
  }

  isEqual(item: KPI): boolean {
    if (item.kpiId !== this.kpiId) return false;
    if (item.currentDefinitionRevisionId !== this.currentDefinitionRevisionId) return false;
    if (item.type !== this.type) return false;
    if (item.decimalCount !== this.decimalCount) return false;
    if (item.commentMode !== this.commentMode) return false;
    if (item.attachmentMode !== this.attachmentMode) return false;
    if (item.required !== this.required) return false;
    if (item.minValue !== this.minValue) return false;
    if (item.maxValue !== this.maxValue) return false;
    if (item.defValue !== this.defValue) return false;
    if (item.expectedResult !== this.expectedResult) return false;
    if (item.instruction !== this.instruction) return false;
    if (item.description !== this.description) return false;
    if (item.auditTrailId !== this.auditTrailId) return false;
    if (item.name !== this.name) return false;
    if (item.listId !== this.listId) return false;
    if (item.authSchemaId !== this.authSchemaId) return false;
    if (item.folderId !== this.folderId) return false;
    if (item.showLabel !== this.showLabel) return false;
    if (item.isField !== this.isField) return false;

    if (
      areDifferent(item.tagIds, this.tagIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    if (
      areDifferent(item.choices, this.choices, (a: StringValue, b: StringValue) => {
        return a.isEqual(b);
      }) === true
    ) {
      return false;
    }

    return true;
  }

  clone(): KPI {
    const output = new KPI();
    output.kpiId = this.kpiId;
    output.currentDefinitionRevisionId = this.currentDefinitionRevisionId;
    output.currentDefinitionRevisionNumber = this.currentDefinitionRevisionNumber;
    output.ownerTaskTypeFamilyId = this.ownerTaskTypeFamilyId;
    output.rowVersion = this.rowVersion;
    output.type = this.type;
    output.decimalCount = this.decimalCount;
    output.commentMode = this.commentMode;
    output.attachmentMode = this.attachmentMode;
    output.required = this.required;
    output.minValue = this.minValue;
    output.maxValue = this.maxValue;
    output.defValue = this.defValue;
    output.expectedResult = this.expectedResult;
    output.instruction = this.instruction;
    output.description = this.description;
    output.auditTrailId = this.auditTrailId;
    output.tagIds = [...this.tagIds];
    output.name = this.name;
    output.transIdx = this.transIdx;
    output.trans = [...this.trans];
    output.attachments = this.attachments?.map((r) => r.clone());
    output.choices = this.choices?.map((c) => c.clone());
    output.data = this.data?.map((d) => d.clone());
    output.systemKPIType = this.systemKPIType;
    output.listId = this.listId;
    output.alerts = this.alerts.map((a) => a.clone());
    output.authSchemaId = this.authSchemaId;
    output.folderId = this.folderId;
    output.showLabel = this.showLabel;
    output.isField = this.isField;
    output.controlIds = this.controlIds ? [...this.controlIds] : undefined;
    output.objectiveIds = this.objectiveIds ? [...this.objectiveIds] : undefined;

    return output;
  }

  getMaxAttachments = (): number => {
    if (!this.attachmentMode) return 0;

    return this.attachmentMode < 0 ? Math.abs(this.attachmentMode) : 5;
  };

  getMinAttachments = (): number => {
    if (!this.attachmentMode) return 0;

    return this.attachmentMode > 0 ? this.attachmentMode : 0;
  };

  getMaxComment = (): number => {
    if (!this.commentMode) return 0;

    return this.commentMode < 0 ? Math.abs(this.commentMode) : 4000;
  };

  getMinComment = (): number => {
    if (!this.commentMode) return 0;

    return this.commentMode > 0 ? this.commentMode : 0;
  };

  getAttachments = (): KPIDataContext[] => {
    if (!this.data || this.data.length === 0 || !this.data[0].contexts) return [];

    return this.data[0].contexts.filter((c) => c.entityType === EntityTypes.KPILink);
  };

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    // Mirrors the API rules that reject the request after submit: KPI_TranslationDTO.Name
    // ([Required], [StringLength(512)]) and KPIDTO.ValidateSuccesError (DefValue must be
    // specified when the label is hidden).
    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().max(512).required().label(localizedFields['name']),
      defValue:
        this.type === KPITypes.SuccesError && !this.showLabel
          ? Joi.string()
              .required()
              .label(localizedFields['defValue'] ?? 'defValue')
          : Joi.any(),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      {
        name: this.name,
        defValue: this.defValue,
      },
      { abortEarly: false },
    );
  }

  isStackedType = (): boolean => {
    return this.type === KPITypes.SuccesError || this.type === KPITypes.Choice;
  };

  isEvidenceType = (): boolean => {
    return !this.isSystemType() && !(this.type === KPITypes.Expression || this.type === KPITypes.Date);
  };

  isCalcType = (): boolean => {
    return (
      this.type === KPITypes.Number ||
      this.type === KPITypes.SuccesError ||
      this.type === KPITypes.Expression ||
      this.type === KPITypes.Choice
    );
  };

  isSystemType = (): boolean => !!this.systemKPIType;

  // the KPI of a SkillControl check: listed in the KPI overview, unlike the other system types
  isExternalCheck = (): boolean => this.systemKPIType === KPITypesSystem.ExternalCheck;

  static async nameExists(orgName: string, appContext: IAppContext): Promise<number> {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    return await apiGetKPIName(orgName, accessToken);
  }
}

export class KPIGraphDataConfig {
  kpiId: number;

  periodStart: Date | undefined;

  periodEnd: Date | undefined;

  aggregationMethod: KPIAggregationMethods;

  aggregationInterval: KPIAggregationIntervals;

  chartType: KPIChartTypes;

  contextFilter?: KPIContextFilter;

  constructor() {
    this.kpiId = 0;
    this.periodStart = undefined;
    this.periodEnd = undefined;
    this.aggregationMethod = KPIAggregationMethods.Count;
    this.aggregationInterval = KPIAggregationIntervals.Month;
    this.chartType = KPIChartTypes.Bar;
  }
}
